import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "../../store"

/**
 * Generic registry for "don't show the global loader right now" - any feature
 * can register a reason (key) under a condition, without GlobalLoader needing
 * to know who's asking or why. Multiple registered keys can be active at
 * once; the loader stays suppressed as long as at least one is.
 *
 * activeKeys - the actual suppression switch, written by useSuppressGlobalLoader
 * (a manual, caller-chosen condition) or automatically by loaderSuppressionMiddleware
 * for thunk args carrying `suppressLoaderKey` (unconditional - suppressed for the
 * whole lifetime of that call).
 *
 * pendingByKey - a plain "is a call tagged with this key in flight" counter,
 * written automatically by loaderSuppressionMiddleware for thunk args carrying
 * `trackLoaderKey`. Read via selectIsKeyPending and combined with whatever
 * condition the caller needs (e.g. a route check) before calling
 * useSuppressGlobalLoader themselves - this is what makes conditional
 * suppression possible without a dedicated tracker slice per feature.
 */
interface LoaderSuppressionState {
  activeKeys: Record<string, boolean>
  pendingByKey: Record<string, number>
}

const initialState: LoaderSuppressionState = {
  activeKeys: {},
  pendingByKey: {},
}

const loaderSuppressionSlice = createSlice({
  name: "loaderSuppressionService",
  initialState,
  reducers: {
    setLoaderSuppression: (
      state,
      action: PayloadAction<{ key: string; isActive: boolean }>,
    ) => {
      const { key, isActive } = action.payload
      if (isActive) {
        state.activeKeys[key] = true
      } else {
        delete state.activeKeys[key]
      }
    },
    incrementPendingKey: (state, action: PayloadAction<string>) => {
      const key = action.payload
      state.pendingByKey[key] = (state.pendingByKey[key] ?? 0) + 1
    },
    decrementPendingKey: (state, action: PayloadAction<string>) => {
      const key = action.payload
      state.pendingByKey[key] = Math.max(0, (state.pendingByKey[key] ?? 0) - 1)
    },
  },
})

export const {
  setLoaderSuppression,
  incrementPendingKey,
  decrementPendingKey,
} = loaderSuppressionSlice.actions

export const selectIsGlobalLoaderSuppressed = (state: RootState): boolean =>
  Object.keys(state.loaderSuppressionService.activeKeys).length > 0

export const selectIsKeyPending =
  (key: string) =>
  (state: RootState): boolean =>
    (state.loaderSuppressionService.pendingByKey[key] ?? 0) > 0

export default loaderSuppressionSlice.reducer
